import { useCallback, useState } from 'react';
import { appEnv } from '../../_env';
import { cleanLocalStorage } from '../../_shared';
import { displayToast } from '../../_shared/components';
import * as storage from '../../_shared/utils/storage';

async function initLoginFlow(return_to: string) {
   // return_to is already decoded here (read via qsParse on the login page); it
   // may itself carry a query string (the OAuth authorize URL), so it MUST be
   // re-encoded before being appended — otherwise Kratos parses return_to only up
   // to the first '&' and drops client_id/redirect_uri/code_challenge/etc.
   const url = `${appEnv.api.loginBrowser}?refresh=true${return_to ? `&return_to=${encodeURIComponent(return_to)}` : ''}`;

   const res = await fetch(url, {
      credentials: 'include',
      redirect: 'manual',
      headers: { Accept: 'application/json' },
   });

   // flow expired → Kratos 303 → retry once should reset the flow and return the new one
   if (res.redirected || !res.headers.get('content-type')?.includes('json')) {
      const retry = await fetch(url, {
         credentials: 'include',
         redirect: 'manual',
         headers: { Accept: 'application/json' },
      });

      // still redirected or not json → fail
      if (retry.redirected || !retry.headers.get('content-type')?.includes('json')) {
         throw new Error('Login initialization failed.');
      }
      return retry.json();
   }

   return res.json();
}

export function useLogin(return_to?: string) {
   const [loginLoading, setLoginLoading] = useState(false);

   const onLoginClick = useCallback(
      (provider: string) => {
         async function doLogin() {
            try {
               setLoginLoading(true);
               const data = await initLoginFlow(return_to || '');

               //TODO validate with io-ts for future internal login flow
               const action = data?.ui?.action;
               /*  not used on external login flow
                  const csrf_token =
                  data?.ui?.nodes?.find((n: { attributes: { name: string } }) => n?.attributes?.name === 'csrf_token')
                     ?.attributes?.value || '';
                */
               if (!action) {
                  throw new Error('Login initialization error!');
               }

               const dataPost = await fetch(action.replace(new URL(action).origin, window.location.origin), {
                  credentials: 'include',
                  method: 'POST',
                  headers: {
                     Accept: 'application/json',
                     'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                     provider,
                  }),
               }).then((d) => d.json());

               if (dataPost?.redirect_browser_to) {
                  /* TODO remove as kratos is fixed */
                  storage.setItem(sessionStorage, 'afterLogin', 'true');
                  /* TODO remove as kratos is fixed */
                  window.location.href = dataPost.redirect_browser_to;
                  return;
               } else {
                  throw new Error(dataPost?.error?.reason || dataPost?.error?.message || 'Login redirect error!');
               }
            } catch (e: unknown) {
               displayToast('error', 'Login Failed!', (e as Error)?.message || 'Unknown Error');
               setLoginLoading(false);
            }
         }
         cleanLocalStorage();
         doLogin();
      },
      [return_to],
   );

   return {
      loginLoading,
      onLoginClick,
   };
}
